import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  DataService,
  NotificationService
} from '@vendure/admin-ui/core';
import { BasicCardData } from '../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { safeJSONParse } from '../utils';
import { Asset } from '../generated';
import { Observable } from 'rxjs';

@Component({
  template: `
    <vdr-card>
      <vdr-form-field [label]="'Card Asset'" class="mb-2">
        <vdr-relation-asset-input
          [parentFormControl]="assetControl"
          [config]="config"
          [readonly]="readonly"
        ></vdr-relation-asset-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Title'" class="mb-2">
        <vdr-dynamic-form-input [control]="titleControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Body Text'">
        <vdr-dynamic-form-input [control]="textControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class BasicCardFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  textValueDef: any = { type: 'string', ui: { component: 'text-value-input', args: { activeLanguageCode: this.activeLanguageCode } } };

  basicCardData: BasicCardData;
  assetControl = new FormControl();
  titleControl = new FormControl();
  textControl = new FormControl();

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    const defaultBasicCardData: BasicCardData = {
      assetId: null,
      title: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
      text: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
    };
    this.basicCardData = safeJSONParse<BasicCardData>(
      this.formControl.value,
      this.notificationService,
      defaultBasicCardData,
    )!;
    this.initializeFormControls();
    // Load the Asset based on the stored ID
    if (this.basicCardData?.assetId) {
      this.dataService.product
        .getAsset(this.basicCardData.assetId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.assetControl.setValue(asset);
        });
    }
  }

  ngAfterViewInit() {
    this.assetControl.valueChanges.subscribe(this.onIconChange);
    this.titleControl.valueChanges.subscribe(this.onTitleChange);
    this.textControl.valueChanges.subscribe(this.onValueChange);
  }

  private initializeFormControls() {
    this.titleControl.setValue(this.toTextValueJson(this.basicCardData?.title));
    this.textControl.setValue(this.toTextValueJson(this.basicCardData?.text));
  }

  private onIconChange = (value: Asset | null) => {
    const assetId = value ? value.id : null;
    if (this.basicCardData?.assetId !== assetId) {
      this.basicCardData!.assetId = assetId;
      this.updateFormControlValue();
    }
  };

  private onTitleChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.basicCardData?.title) !== JSON.stringify(next)) {
      this.basicCardData!.title = next;
      this.updateFormControlValue();
    }
  };

  private onValueChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.basicCardData?.text) !== JSON.stringify(next)) {
      this.basicCardData!.text = next;
      this.updateFormControlValue();
    }
  };

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  private updateFormControlValue() {
    this.formControl.setValue(JSON.stringify(this.basicCardData));
    this.formControl.markAsDirty();
  }
}
